<template>
    <widget-card
        :icon="QrCode"
        :info-tooltip="trans('dashboard.v2.operational_availability.info')"
        :loading="loading"
        :title="trans('dashboard.v2.operational_availability.title')"
    >
        <template v-if="lastUpdated" #header-subtitle>
            <div class="small mt-1 text-muted">{{ trans('dashboard.v2.last_update') }}: {{ lastUpdated }}</div>
        </template>
        <template #header-end>
            <div class="d-flex align-items-center gap-2">
                <jet-button v-if="showDebug" class="btn-icon" variant="secondary" @click="showDebugInfo = !showDebugInfo">
                    <icon name="cil-bug" />
                </jet-button>
            </div>
        </template>
        <div class="h-100">
            <div class="d-flex flex-column flex-md-row align-items-stretch h-100">
                <chart-loading-skeleton v-if="loading" />
                <empty-widget-state v-else-if="!hasData" :text="trans('dashboard.v2.operational_availability.empty_state')" />
                <template v-else>
                    <c-chart
                        :key="componentKey"
                        :custom-tooltips="false"
                        :data="chartData"
                        :options="chartOptions"
                        :plugins="[ChartDataLabels]"
                        class="grow"
                        type="bar"
                    />
                    <ChartLegend :legend-items="legendItems" :loading="loading" />
                </template>
            </div>
        </div>
    </widget-card>
    <debug-view v-if="showDebug" v-model="showDebugInfo" title="Operational Availability<br/><small>Debug Info</small>">
        <pre style="background-color: #fefefe">{{ debugInfo }}</pre>
    </debug-view>
</template>

<script setup>
import { computed, toRef } from 'vue';
import WidgetCard from '@/Pages/DashboardV2/Components/WidgetCard.vue';
import ChartLoadingSkeleton from '@/Pages/DashboardV2/Components/ChartLoadingSkeleton.vue';
import ChartLegend from '@/Pages/DashboardV2/Components/ChartLegend.vue';
import EmptyWidgetState from '@/Pages/DashboardV2/Components/EmptyWidgetState.vue';
import { CChart } from '@coreui/vue-chartjs';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { trans } from '@/mixins/i18n';
import QrCode from '../../../../icons/QrCode.vue';
import { formatPercentage } from '@/utils.js';
import DebugView from '@/Components/DebugView.vue';
import JetButton from '@/Jetstream/Button.vue';
import { useChartWidget } from '@/composables/useChartWidget';
import { getBaseChartOptions } from '@/Utils/chartDefaults';

const props = defineProps({
    widgetData: {
        type: Object,
        required: true,
        default: () => ({}),
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
});

const {
    data,
    loading,
    componentKey,
    hasData: hasDataComputed,
    lastUpdated,
    selectedGrouping,
    groupingOptions,
    debug: showDebug,
    showDebugInfo,
    debugInfo,
} = useChartWidget({
    dataUrl: computed(() => props.widgetData?.dataUrl),
    filters: toRef(props, 'filters'),
    parseResponse: (response) => ({
        data: {
            labels: response.data.labels,
            types: response.data.types,
            actualUptimeData: response.data.actualUptimeData,
            slaData: response.data.slaData,
        },
        lastUpdated: response.data.lastUpdated,
        debug: response.data.debug,
    }),
    defaultGrouping: 'type',
});

// Extract data from the response
const labels = computed(() => data.value?.labels || []);
const types = computed(() => data.value?.types || []);
const actualUptime = computed(() => data.value?.actualUptimeData || []);
const slaUptime = computed(() => data.value?.slaData || []);

const hasData = computed(() => labels.value.length > 0);

const legendItems = computed(() => [
    {
        key: 'sla',
        text: trans('dashboard.v2.operational_availability.legend.sla'),
        color: '#10b981',
        shape: 'circle',
    },
    {
        key: 'actual',
        text: trans('dashboard.v2.operational_availability.legend.actual'),
        color: '#3b82f6',
        shape: 'square',
    },
]);

const chartData = computed(() => {
    if (!hasData.value) {
        return {
            labels: [],
            datasets: [],
        };
    }

    return {
        labels: types.value,
        datasets: [
            {
                type: 'bar',
                label: trans('dashboard.v2.operational_availability.legend.actual'),
                order: 1,
                backgroundColor: (context) => {
                    const index = context.dataIndex;
                    const slaValue = slaUptime.value[index];

                    if (slaValue === null) {
                        return '#2C54EA'; // Blue
                    }

                    if (actualUptime.value[index] >= slaValue) {
                        return '#7BD3A4'; // Green
                    }
                    return '#2C54EA'; // Blue
                },
                borderRadius: 4,
                data: actualUptime.value,
                barPercentage: 0.6,
                datalabels: {
                    display: (context) => {
                        if (actualUptime.value[context.dataIndex] === 100) return false;
                        const slaValue = slaUptime.value[context.dataIndex];
                        if (slaValue === null) return 'auto';
                        return actualUptime.value[context.dataIndex] < slaValue;
                    },
                    direction: 'horizontal',
                    clamp: false,
                    anchor: 'middle',
                    align: 'center',
                    backgroundColor: '#3B82F6',
                    borderRadius: 2,
                    color: 'white',
                    font: {
                        size: 10,
                        weight: 'bold',
                    },
                    formatter: (value) => formatPercentage(value),
                    padding: {
                        top: 2,
                        bottom: 2,
                        left: 4,
                        right: 4,
                    },
                },
            },
            {
                type: 'scatter',
                label: trans('dashboard.v2.operational_availability.legend.sla'),
                order: 0,
                data: slaUptime.value.map((value) => value ?? 0),
                backgroundColor: '#7BD3A4',
                pointRadius: (context) => {
                    const slaValue = slaUptime.value[context.dataIndex];
                    if (slaValue === null) return 0;
                    return actualUptime.value[context.dataIndex] >= slaValue ? 6 : 5;
                },
                pointBorderColor: 'white',
                pointBorderWidth: (context) => (actualUptime.value[context.dataIndex] >= slaUptime.value[context.dataIndex] ? 1 : 0),
                pointHoverRadius: (context) => (actualUptime.value[context.dataIndex] >= slaUptime.value[context.dataIndex] ? 6 : 5),
                pointHoverBorderWidth: (context) => (actualUptime.value[context.dataIndex] >= slaUptime.value[context.dataIndex] ? 2 : 0),
                clip: false,
                datalabels: {
                    display: (context) => {
                        return slaUptime.value[context.dataIndex] !== null ? 'auto' : false;
                    },
                    anchor: 'end',
                    align: 'top',
                    offset: 12,
                    backgroundColor: '#D3FEE7',
                    borderRadius: 4,
                    color: '#08C55A',
                    font: {
                        size: 10,
                        weight: 'bold',
                    },
                    formatter: (value) => formatPercentage(value),
                    padding: {
                        top: 2,
                        bottom: 2,
                        left: 4,
                        right: 4,
                    },
                },
            },
        ],
    };
});

const chartOptions = computed(() => {
    return {
        ...getBaseChartOptions(),
        animation: false,
        plugins: {
            ...getBaseChartOptions().plugins,
            tooltip: {
                enabled: true,
                intersect: false,
                mode: 'index',
                callbacks: {
                    title: (context) => labels.value[context[0].dataIndex],
                    label: (context) => {
                        const isSlaDataset = context.dataset.label === trans('dashboard.v2.operational_availability.legend.sla');
                        const slaValue = slaUptime.value[context.dataIndex];

                        if (isSlaDataset && slaValue === null) {
                            return trans('dashboard.v2.operational_availability.legend.sla.no_sla');
                        }

                        return `${context.dataset.label}: ${formatPercentage(context.parsed.y)}`;
                    },
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    autoSkip: true,
                    maxRotation: 45,
                    minRotation: 0,
                    font: {
                        size: 11,
                    },
                },
            },
            y: {
                beginAtZero: true,
                max: 100,
                grace: '5%',
                grid: {
                    drawBorder: false,
                },
                ticks: {
                    callback: (value) => `${value}%`,
                },
            },
        },
        layout: {
            padding: {
                top: 40,
            },
        },
    };
});
</script>
